// WebSocket 实时通道:同源 HttpOnly cookie 自动鉴权,自动重连,多订阅者
type MessageHandler = (type: string, data: any) => void;
type StatusHandler = (status: 'connecting' | 'connected' | 'disconnected') => void;

class ChatSocket {
  private ws: WebSocket | null = null;
  private handlers = new Set<MessageHandler>();
  private statusHandlers = new Set<StatusHandler>();
  private retryTimer: number | null = null;
  private closedByUser = false;
  private status: 'connecting' | 'connected' | 'disconnected' = 'disconnected';

  connect() {
    if (this.ws?.readyState === WebSocket.OPEN) return;
    this.closedByUser = false;
    this.open();
  }

  private setStatus(s: 'connecting' | 'connected' | 'disconnected') {
    if (this.status === s) return;
    this.status = s;
    this.statusHandlers.forEach((h) => h(s));
  }

  private open() {
    this.setStatus('connecting');
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    // 开发环境:webpack HMR 占用同源 /ws,聊天 WS 直连后端(会话 cookie 按 host 作用域,localhost 下跨端口仍会携带)
    // 生产环境:同源 /ws,由反向代理转发到后端
    const isDev = process.env.NODE_ENV === 'development';
    const url = isDev
      ? `${proto}://localhost:8090/ws`
      : `${proto}://${window.location.host}/ws`;
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => {
      if (this.retryTimer) {
        clearTimeout(this.retryTimer);
        this.retryTimer = null;
      }
      this.setStatus('connected');
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data as string) as { type: string; data: any };
        this.handlers.forEach((h) => h(msg.type, msg.data));
      } catch {
        /* ignore */
      }
    };
    ws.onclose = () => {
      this.setStatus('disconnected');
      if (this.closedByUser) return;
      this.retryTimer = window.setTimeout(() => this.open(), 2000);
    };
    ws.onerror = () => ws.close();
  }

  onMessage(handler: MessageHandler): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  onStatusChange(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    handler(this.status);
    return () => {
      this.statusHandlers.delete(handler);
    };
  }

  getStatus() {
    return this.status;
  }

  send(type: string, data: unknown): boolean {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, data }));
      return true;
    }
    return false;
  }

  close() {
    this.closedByUser = true;
    this.ws?.close();
    this.ws = null;
    this.setStatus('disconnected');
  }
}

export const chatSocket = new ChatSocket();
